import { ApiClient } from "@/services/apiClient";
import { ProfileDataSavingException } from "@/services/user/settings/exceptions";
import { type UserSettings, type UpdateUserSettingsData } from "@/services/user/settings/types";
import type { AxiosError } from "axios";
import { gettext } from "@/services/translation";
import { UserNotEstablishedException } from "@/services/user/userExceptions";
import type { CategoryId } from "@/services/collection/categories";
import { useUserSettings } from "@/composables/user";
import { UserSettings_Schema } from "@/services/user/settings/userSettingsSchema";

export const updateUserSettings = async (data: UpdateUserSettingsData): Promise<void> =>
  ApiClient.put<UserSettings>("api/user/collector", {}, data)
    .then(({ data: updatedSettings }) => {
      const { userSettings } = useUserSettings();
      userSettings.value = updatedSettings;
    })
    .catch((error: AxiosError) => {
      const status = error.response?.status;
      if (status === 400) {
        const msg = gettext.$gettext("Nastavení se nepodařilo uložit.");
        throw new ProfileDataSavingException(msg);
      } else if (status === 404) {
        const msg = gettext.$gettext("Tohoto uživatele neznáme.");
        throw new ProfileDataSavingException(msg);
      } else if (status === 409) {
        const data = error.response?.data as { error: string };
        const type = data.error;
        let msg = "Unknown error.";
        if (type && type === "nickname_taken") {
          msg = gettext.$gettext("Bohužel, přezdívku už používá někdo jiný. Zkus nějakou jinou.");
        } else if (type && type === "nickname_too_early_change") {
          msg = gettext.$gettext("Přezdívku zatím nelze změnit.");
        }

        throw new ProfileDataSavingException(msg);
      }

      throw error;
    });

export const fetchUserSettings = async (): Promise<UserSettings> =>
  ApiClient.get<UserSettings>("api/user/collector")
    .then(({ data }) => UserSettings_Schema.parse(data))
    .catch((error) => {
      const status = error.response?.status;
      const UserNotEstablishedStatuses = [400, 404];
      if (UserNotEstablishedStatuses.includes(status)) {
        throw new UserNotEstablishedException();
      } else {
        throw error;
      }
    });

type CreateUserSettingsData = {
  nickname: string;
  categoryId: CategoryId[];
};

export const createUserSettings = async (data: CreateUserSettingsData): Promise<UserSettings> =>
  ApiClient.post<UserSettings>("api/user/collector", {}, data)
    .then(({ data: newSettings }) => (useUserSettings().userSettings.value = newSettings))
    .catch((error: AxiosError) => {
      const status = error.response?.status;
      if (status === 400) {
        const msg = gettext.$gettext("Nastavení se nepodařilo uložit.");
        throw new ProfileDataSavingException(msg);
      } else if (status === 409) {
        const msg = gettext.$gettext("Bohužel, přezdívku už používá někdo jiný. Zkus nějakou jinou.");
        throw new ProfileDataSavingException(msg);
      }

      throw error;
    });
